import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import api from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, Clock as ClockIcon, ShieldQuestion, RefreshCw, ExternalLink, KeyRound, Save, Eraser, RotateCcw } from "lucide-react";

/**
 * Compliance queue for the KYCAID Phase 0 bridge.
 *
 * Operators land here to:
 *   1. See who just verified (gate before manually emailing the XGate invoice link).
 *   2. Triage failures + see decline reasons.
 *   3. See submitted applicants that are stuck.
 *   4. See in_progress applicants who opened the form but never uploaded docs
 *      (v36 — used to be conflated with `submitted`, which made it impossible
 *      to tell "stuck on our side" from "stuck on KYCAID's side").
 *
 * Backend: `GET /api/kycaid/queue?state=verified|submitted|in_progress|failed|unverified`
 */
const TABS = [
  { value: "verified",    label: "Verified",     Icon: ShieldCheck,    tone: "emerald" },
  { value: "submitted",   label: "Submitted",    Icon: ClockIcon,      tone: "sky" },
  { value: "in_progress", label: "In progress",  Icon: RotateCcw,      tone: "amber" },
  { value: "failed",      label: "Failed",       Icon: ShieldX,        tone: "rose" },
  { value: "unverified",  label: "Unstarted",    Icon: ShieldQuestion, tone: "slate" },
];

const TONE = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  slate:   "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminKycaid() {
  const navigate = useNavigate();
  const [state, setState] = useState("verified");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Back-office config
  const [cfg, setCfg] = useState(null);
  const [cfgEditOpen, setCfgEditOpen] = useState(false);
  const [cfgDraft, setCfgDraft] = useState({ api_token: "", form_id: "", api_base: "" });
  const [cfgSaving, setCfgSaving] = useState(false);

  const loadCfg = async () => {
    try {
      const r = await api.get("/kycaid/config");
      setCfg(r.data);
    } catch (e) { /* silent */ }
  };

  const saveCfg = async () => {
    setCfgSaving(true);
    try {
      // Only send fields that the operator actually touched.
      const patch = {};
      if (cfgDraft.api_token !== "") patch.api_token = cfgDraft.api_token;
      if (cfgDraft.form_id   !== "") patch.form_id   = cfgDraft.form_id;
      if (cfgDraft.api_base  !== "") patch.api_base  = cfgDraft.api_base;
      const r = await api.patch("/kycaid/config", patch);
      setCfg(r.data);
      setCfgEditOpen(false);
      setCfgDraft({ api_token: "", form_id: "", api_base: "" });
      toast.success("KYCAID config updated. New creds are live without a restart.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update config");
    } finally { setCfgSaving(false); }
  };

  const clearCfg = async () => {
    if (!window.confirm("Clear KYCAID overrides? The system will fall back to env vars.")) return;
    try {
      const r = await api.patch("/kycaid/config", { api_token: "", form_id: "", api_base: "" });
      setCfg(r.data);
      toast.success("Overrides cleared. Falling back to env vars.");
    } catch (e) { toast.error("Failed to clear"); }
  };

  const load = async (s = state) => {
    setLoading(true);
    try {
      const r = await api.get(`/kycaid/queue?state=${s}`);
      setRows(r.data?.applicants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load queue");
    } finally { setLoading(false); }
  };

  /* eslint-disable */
  useEffect(() => { load(state); }, [state]);
  useEffect(() => { loadCfg(); }, []);
  /* eslint-enable */

  const counts = rows.length;

  return (
    <AdminLayout
      title="KYCAID verification queue"
      subtitle="Phase 0 compliance gate. Verify here before issuing the XGate invoice link manually."
      actions={
        <Button onClick={() => load(state)} variant="outline" size="sm" className="rounded-xl gap-1.5"
                data-testid="kycaid-queue-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      }
    >
      <Tabs value={state} onValueChange={setState} className="w-full">
        <TabsList className="bg-muted/40 rounded-xl mb-4" data-testid="kycaid-queue-tabs">
          {TABS.map((t) => {
            const Icon = t.Icon;
            return (
              <TabsTrigger key={t.value} value={t.value} className="gap-1.5" data-testid={`kycaid-tab-${t.value}`}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Back-office runtime config — operators can rotate creds without a restart */}
      {cfg && (
        <div className="xch-card p-4 mb-5" data-testid="kycaid-config-card">
          <div className="flex items-start gap-3 flex-wrap">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 border ${cfg.wired ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              <KeyRound className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-display font-bold text-foreground">KYCAID API connection</div>
                <Badge variant="outline"
                       className={`text-[10px] uppercase tracking-wider font-bold ${cfg.wired ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}
                       data-testid="kycaid-config-wired-badge">
                  {cfg.wired ? "Wired" : "Not wired"}
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-1 mt-2 text-xs">
                <div className="text-muted-foreground">API token</div>
                <div className="font-mono text-[11px] text-foreground md:col-span-2" data-testid="kycaid-config-token-mask">
                  {cfg.api_token_set ? `${cfg.api_token_mask}` : <em className="text-muted-foreground">not set</em>}
                  <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.api_token})</span>
                </div>
                <div className="text-muted-foreground">Form ID</div>
                <div className="font-mono text-[11px] text-foreground md:col-span-2" data-testid="kycaid-config-form-id">
                  {cfg.form_id || <em className="text-muted-foreground">not set</em>}
                  <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.form_id})</span>
                </div>
                <div className="text-muted-foreground">API base</div>
                <div className="font-mono text-[11px] text-foreground md:col-span-2">
                  {cfg.api_base}
                  <span className="ml-2 text-[10px] text-muted-foreground">(source: {cfg.source.api_base})</span>
                </div>
              </div>
              {!cfgEditOpen && (
                <div className="flex gap-2 mt-3">
                  <Button onClick={() => setCfgEditOpen(true)} variant="outline" size="sm"
                          className="rounded-xl gap-1.5" data-testid="kycaid-config-edit-btn">
                    <KeyRound className="w-3.5 h-3.5" /> Rotate / set creds
                  </Button>
                  {(cfg.source.api_token === "settings" || cfg.source.form_id === "settings" || cfg.source.api_base === "settings") && (
                    <Button onClick={clearCfg} variant="outline" size="sm"
                            className="rounded-xl gap-1.5" data-testid="kycaid-config-clear-btn">
                      <Eraser className="w-3.5 h-3.5" /> Clear overrides
                    </Button>
                  )}
                </div>
              )}
              {cfgEditOpen && (
                <div className="mt-3 space-y-2.5 rounded-xl border border-border bg-muted/30 p-3" data-testid="kycaid-config-edit-form">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">API token</Label>
                    <Input type="password" value={cfgDraft.api_token}
                           onChange={(e) => setCfgDraft({ ...cfgDraft, api_token: e.target.value })}
                           placeholder="kycaid_live_… (paste new token)"
                           className="rounded-xl mt-1" data-testid="kycaid-config-token-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Form ID</Label>
                    <Input value={cfgDraft.form_id}
                           onChange={(e) => setCfgDraft({ ...cfgDraft, form_id: e.target.value })}
                           placeholder="e.g. b9f1e0c2-…"
                           className="rounded-xl mt-1" data-testid="kycaid-config-form-input" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">API base (optional override)</Label>
                    <Input value={cfgDraft.api_base}
                           onChange={(e) => setCfgDraft({ ...cfgDraft, api_base: e.target.value })}
                           placeholder="https://api.kycaid.com/v1"
                           className="rounded-xl mt-1" data-testid="kycaid-config-base-input" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button onClick={() => { setCfgEditOpen(false); setCfgDraft({ api_token: "", form_id: "", api_base: "" }); }}
                            variant="ghost" size="sm" className="rounded-xl">Cancel</Button>
                    <Button onClick={saveCfg} disabled={cfgSaving || (!cfgDraft.api_token && !cfgDraft.form_id && !cfgDraft.api_base)}
                            className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                            data-testid="kycaid-config-save-btn">
                      <Save className="w-3.5 h-3.5" /> {cfgSaving ? "Saving…" : "Save"}
                    </Button>
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    Leave a field blank to keep its current value. Changes take effect immediately — no restart required.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="xch-card overflow-hidden" data-testid="kycaid-queue-list">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : counts === 0 ? (
          <div className="p-12 text-center">
            <div className="text-sm font-display font-bold text-foreground">No applicants in “{state}”.</div>
            <div className="text-xs text-muted-foreground mt-1">Switch tabs or hit refresh.</div>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {rows.map((r) => {
              const tone = TABS.find((t) => t.value === state)?.tone || "slate";
              return (
                <div key={r.id} className="p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors"
                     data-testid={`kycaid-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-foreground flex items-center gap-2 flex-wrap">
                      {r.name || r.email}
                      <Badge variant="outline" className="text-[10px] uppercase font-bold">{r.country || "—"}</Badge>
                      <Badge variant="outline" className={`text-[10px] uppercase font-bold ${TONE[tone]}`}>
                        {r.kycaid_state}
                      </Badge>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">{r.email}</div>
                    <div className="text-[10px] font-mono text-muted-foreground truncate mt-0.5">
                      applicant: {r.kycaid_applicant_id || "—"} · verification: {r.kycaid_verification_id || "—"}
                    </div>
                    {(r.kycaid_decline_reasons || []).length > 0 && (
                      <div className="text-[11px] text-rose-700 mt-1">
                        ↳ {(r.kycaid_decline_reasons || []).join("; ")}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] text-muted-foreground">
                      {r.kycaid_state_at ? new Date(r.kycaid_state_at).toLocaleString("en-IE") : "—"}
                    </div>
                    <Button onClick={() => navigate(`/admin/customers/${r.id}`)}
                            variant="outline" size="sm" className="rounded-xl mt-1.5 gap-1"
                            data-testid={`kycaid-open-360-${r.id}`}>
                      View 360 <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4 text-[11px] text-muted-foreground" data-testid="kycaid-queue-footer">
        Manual invoicing reminder: only customers in the <strong>Verified</strong> tab are cleared for an XGate invoice link.
        Continue to email links manually — automated invoice creation is Phase 1.
      </div>
    </AdminLayout>
  );
}
